import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Navbar from '../../Navbar';
import PageTitle from '../../components/PageTitle';
import RichTextEditor from '../../components/RichTextEditor';
import GuideOutlineSidebar from './GuideOutlineSidebar';
import GuideTitleEditor from './GuideTitleEditor';
import {
  authHeaders,
  extractHeadingsFromHtml,
  stampGuideHeadingIds,
  parseGuideTitleHtml,
  splitGuideDocumentHtml,
  composeGuideDocumentHtml,
  guidesFetch,
  stripGuidePlaceholdersInPlace,
  insertGuideSection,
  removeGuideSection,
  moveGuideSection
} from './guidesUtils';
import { GuideAutosaveIndicator, useGuideDocumentAutosave } from './guideAutosave';
import { printGuideDocument } from './printGuideDocument';
import './guides.css';

export default function TopicEditorPage() {
  const { id, topicId } = useParams();
  const navigate = useNavigate();
  const [topic, setTopic] = useState(null);
  const [notebook, setNotebook] = useState(null);
  const [contentHtml, setContentHtml] = useState('');
  const [titleHtml, setTitleHtml] = useState('');
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [outlineToggleHost, setOutlineToggleHost] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await guidesFetch(`/topics/${topicId}`, { headers: authHeaders() });
      if (!data.canEdit) {
        navigate(`/guides/notebooks/${id}/read/${topicId}`, { replace: true });
        return;
      }
      setTopic(data);
      setNotebook(data.section?.notebook || null);
      const split = splitGuideDocumentHtml(data.contentHtml || '', { fallbackTitle: data.title || '' });
      setTitle(split.titleText || data.title || '');
      setTitleHtml(split.titleHtml);
      setContentHtml(extractHeadingsFromHtml(split.bodyHtml).htmlWithIds);
    } catch (err) {
      toast.error(err.message || 'Failed to load topic');
      navigate(`/guides/notebooks/${id}`);
    } finally {
      setLoading(false);
    }
  }, [topicId, id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const { headings, topicHeadingLevel } = useMemo(
    () => extractHeadingsFromHtml(contentHtml),
    [contentHtml]
  );

  useLayoutEffect(() => {
    const root = document.querySelector('.guides-topic-editor-col .rte-editable-root');
    if (!stampGuideHeadingIds(root, headings)) return;
    const next = root.innerHTML;
    setContentHtml((current) => (current === next ? current : next));
  }, [headings]);
  const titleParsed = useMemo(() => parseGuideTitleHtml(titleHtml), [titleHtml]);
  const composedHtml = useMemo(
    () => composeGuideDocumentHtml(titleHtml, contentHtml),
    [titleHtml, contentHtml]
  );

  const scrollToHeading = (headingId) => {
    const safeId = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(headingId) : headingId;
    const el = document.querySelector(`.guides-topic-editor-col [id="${safeId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const applyContent = (nextHtml, scrollId = null) => {
    const { htmlWithIds } = extractHeadingsFromHtml(nextHtml);
    setContentHtml(htmlWithIds);
    if (scrollId) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => scrollToHeading(scrollId));
      });
    }
  };

  const titleRef = useRef(title);
  titleRef.current = title;
  const topicIdRef = useRef(topicId);
  topicIdRef.current = topicId;

  const persist = useCallback(async (html) => {
    const saveId = topicId;
    try {
      const split = splitGuideDocumentHtml(html, { fallbackTitle: titleRef.current });
      const { htmlWithIds } = extractHeadingsFromHtml(split.bodyHtml);
      const resolvedTitle = split.titleText || String(titleRef.current || '').trim();
      const composed = composeGuideDocumentHtml(split.titleHtml, htmlWithIds);
      await guidesFetch(`/topics/${saveId}`, {
        method: 'PUT',
        headers: authHeaders(true),
        body: JSON.stringify({ title: resolvedTitle || titleRef.current, contentHtml: composed })
      });
      if (String(topicIdRef.current) !== String(saveId)) return { html };
      // Do not write the saved HTML back into the editors. That string is a
      // reparsed copy, and it can be older than keystrokes made while the
      // request was in flight. Either one replaces the document and moves the cursor.
      return { html };
    } catch (err) {
      toast.error(err.message || 'Failed to save');
      throw err;
    }
  }, [topicId]);

  const { status: saveStatus, savedAt, saveNow } = useGuideDocumentAutosave({
    html: composedHtml,
    enabled: !loading,
    resetKey: topicId,
    persist
  });

  const save = async () => {
    try {
      await saveNow();
      toast.success('Saved');
    } catch {
      /* persist already toasted */
    }
  };

  const handleTitleChange = (html) => {
    setTitleHtml(html);
    setTitle(parseGuideTitleHtml(html).text);
  };

  const handleAddTopic = (name, afterHeadingId) => {
    const { html, id: newId } = insertGuideSection(contentHtml, {
      text: name,
      outlineLevel: 1,
      afterHeadingId,
      topicHeadingLevel
    });
    applyContent(html, newId);
  };

  const handleAddSubtopic = (name, afterHeadingId) => {
    const { html, id: newId } = insertGuideSection(contentHtml, {
      text: name,
      outlineLevel: 2,
      afterHeadingId: afterHeadingId || headings.find((h) => h.outlineLevel === 1)?.id || null,
      topicHeadingLevel
    });
    applyContent(html, newId);
  };

  const handleAddLowerSubtopic = (name, afterHeadingId) => {
    const { html, id: newId } = insertGuideSection(contentHtml, {
      text: name,
      outlineLevel: 3,
      afterHeadingId,
      topicHeadingLevel
    });
    applyContent(html, newId);
  };

  const handleRemoveSection = (headingId) => {
    applyContent(removeGuideSection(contentHtml, headingId, topicHeadingLevel));
  };

  const handleMoveSection = (sourceId, targetId) => {
    const next = moveGuideSection(contentHtml, sourceId, targetId, topicHeadingLevel);
    if (next !== contentHtml) applyContent(next, sourceId);
  };

  const goSection = (section) => {
    const topics = [...(section.topics || [])].sort((a, b) => a.sortOrder - b.sortOrder);
    const first = topics[0];
    if (!first) return;
    navigate(`/guides/notebooks/${id}/topics/${first.id}/edit`);
  };

  if (loading) {
    return (
      <div className="guides-app guides-loading-screen">
        <Navbar pageTitle="Edit topic" />
        <div className="guides-loading"><Spinner /></div>
      </div>
    );
  }

  const sections = [...(notebook?.sections || [])].sort((a, b) => a.sortOrder - b.sortOrder);
  const displayTitle = title || 'Edit topic';

  return (
    <div className="guides-app guides-topic-editor-page bg-inverse">
      <Navbar pageTitle={displayTitle} />
      <PageTitle title={displayTitle} />
      <div className="bg-white border-bottom guides-topic-editor-topbar">
        <Container fluid="lg">
          <div className="d-flex justify-content-between align-items-start gap-3 py-2 flex-wrap">
            <div className="d-flex align-items-start gap-3 min-w-0">
              <div ref={setOutlineToggleHost} className="d-flex d-md-none" />
              <Button size="sm" variant="light-solid" className="guides-icon-btn mt-1 d-none d-md-inline-flex" onClick={() => navigate(`/guides/notebooks/${id}`)}>
                <i className="bi bi-chevron-left" />
              </Button>
              <div className="min-w-0">
                <h6 className="mb-0 fw-semibold">{displayTitle}</h6>
                <div className="small mt-0 text-muted">
                  {notebook?.title} · {topic?.section?.title}
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <GuideAutosaveIndicator status={saveStatus} savedAt={savedAt} />
              <Button
                size="sm"
                variant="light-solid"
                onClick={() => window.dispatchEvent(new CustomEvent('guides-reference-library', {
                  detail: { targetType: 'topic', targetId: Number(topicId) }
                }))}
              >
                <i className="bi bi-journal-text me-1" />
                References
              </Button>
              <Button
                size="sm"
                variant="light-solid"
                onClick={() => printGuideDocument({ title: displayTitle })}
                title="Print"
              >
                <i className="bi bi-printer me-1" />
                Print
              </Button>
              <Button size="sm" variant="outline-success" onClick={save} disabled={saveStatus === 'saving'}>
                <i className="bi bi-floppy me-1" />
                {saveStatus === 'saving' ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </Container>
      </div>

      <Container fluid="lg" className="guides-topic-editor-workspace">
        <Row className="g-0 h-100">
          <Col lg={2} md={3} className="border-end guides-topic-editor-rail d-none d-md-block">
            <GuideOutlineSidebar
              headings={headings}
              titleFallback={title}
              titleEmpty={titleParsed.empty}
              topicHeadingLevel={topicHeadingLevel}
              onScrollTo={scrollToHeading}
              onAddTopic={handleAddTopic}
              onAddSubtopic={handleAddSubtopic}
              onAddLowerSubtopic={handleAddLowerSubtopic}
              onRemoveSection={handleRemoveSection}
              onMoveSection={handleMoveSection}
              mobileToggleHost={outlineToggleHost}
            >
            <div className="guides-outline-notebook">
              <div className="guides-outline-kicker mb-2">Notebook topics</div>
              <nav className="guides-read-nav">
                {sections.map((section) => {
                  const sectionTopics = [...(section.topics || [])]
                    .sort((a, b) => a.sortOrder - b.sortOrder);
                  const isSectionOpen = sectionTopics.some(
                    (t) => Number(t.id) === Number(topicId)
                  );
                  return (
                    <div
                      key={section.id}
                      className={`guides-read-section${isSectionOpen ? ' open' : ''}`}
                    >
                      <button
                        type="button"
                        className="guides-read-section-btn"
                        onClick={() => goSection(section)}
                        disabled={sectionTopics.length === 0}
                      >
                        <span className="guides-read-section-btn-label">{section.title}</span>
                        {sectionTopics.length > 0 && (
                          <i
                            className={`bi ${isSectionOpen ? 'bi-chevron-down' : 'bi-chevron-right'} guides-read-topic-chevron`}
                            aria-hidden="true"
                          />
                        )}
                      </button>
                      {isSectionOpen && (
                        <ul className="guides-toc">
                          {sectionTopics.map((t) => (
                            <li
                              key={t.id}
                              className={Number(t.id) === Number(topicId) ? 'active' : ''}
                            >
                              <Link to={`/guides/notebooks/${id}/topics/${t.id}/edit`}>{t.title}</Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </nav>
            </div>
            </GuideOutlineSidebar>
          </Col>
          <Col lg={10} md={9} className="guides-topic-editor-col border-end">
            <RichTextEditor
              value={contentHtml}
              onChange={setContentHtml}
              prepareDomBeforeEmit={stripGuidePlaceholdersInPlace}
              placeholder="Write your topic…"
              variant="document"
              references={{
                targetType: 'topic',
                targetId: Number(topicId),
                programId: notebook?.programId
              }}
              documentHeader={(
                <GuideTitleEditor
                  value={titleHtml}
                  onChange={handleTitleChange}
                  onRequestFocusBody={() => {
                    document.querySelector('.guides-topic-editor-col .rte-editable-root')?.focus();
                  }}
                />
              )}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
}
